/**
 * @file ShipmentDeleteAction.tsx
 * @description Кнопка «Удалить» у отгрузки-черновика с подтверждением.
 *
 * Отдельный компонент, чтобы карточка отгрузки не тащила в себя состояние
 * модалки. Показывается только у черновика: у переданной перевозчику отгрузки
 * сервер всё равно ответит 422 `shipment_not_draft`.
 */

import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface Props {
  /** Идёт любое действие над отгрузками — блокируем кнопки. */
  acting: boolean;
  /** `onDone` вызывается после запроса (успех или ошибка) — закрыть диалог. */
  onDelete: (onDone: () => void) => void;
}

export function ShipmentDeleteAction({ acting, onDelete }: Props) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function confirm(): void {
    setDeleting(true);
    onDelete(() => {
      setDeleting(false);
      setOpen(false);
    });
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 text-destructive hover:bg-destructive/10"
        onClick={() => setOpen(true)}
        disabled={acting}
      >
        <Trash2 className="mr-1 size-4" />
        Удалить
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && !deleting && setOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Удалить отгрузку?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Позиции вернутся в заказ, действие необратимо.
          </p>
          <DialogFooter>
            <Button
              variant="ghost"
              className="min-h-11"
              onClick={() => setOpen(false)}
              disabled={deleting}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              className="min-h-11"
              onClick={confirm}
              disabled={deleting}
            >
              {deleting ? "Удаляем…" : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
